import React, {useState, useEffect, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {Form, Alert, Card, Row, Col, Button, Pagination} from 'react-bootstrap'
import {useDispatch, useSelector} from 'react-redux'
import {KTCard, KTCardBody, Fancybox} from '../../../components'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'
import {projectLookup} from '../../../redux/slices/lookup.slice'
import {reportPhotosGetAll} from '../../../redux/slices/workplan.slice'

function List() {
  const intl = useIntl()
  const dispatch = useDispatch()

  const selectData = useMemo(
    () => (state) => ({
      reportPhotos: state.workplan.reportPhotos,
      loading: state.workplan.loading,
      error: state.workplan.error,
    }),
    []
  )

  const selectLookupData = useMemo(
    () => (state) => ({
      projectLookupList: state.lookup.projectLookupList,
    }),
    []
  )

  const {reportPhotos, loading, error} = useSelector(selectData)
  const {projectLookupList} = useSelector(selectLookupData)

  const [projectId, setProjectId] = useState('')
  const [description, setDescription] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const fetchPhotos = (page) => {
    dispatch(
      reportPhotosGetAll({
        projectId: projectId ? parseInt(projectId) : undefined,
        description: description || undefined,
        page,
        items_per_page: 32,
      })
    )
  }

  useEffect(() => {
    dispatch(projectLookup())
    fetchPhotos(1)
  }, [dispatch])

  const handleDownload = async (url, fileName) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = fileName || 'photo'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
    } catch {
      window.open(url, '_blank')
    }
  }

  const handleFilter = () => {
    setCurrentPage(1)
    fetchPhotos(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    fetchPhotos(page)
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const items = reportPhotos?.data ?? []
  const pagination = reportPhotos?.payload?.pagination
  const totalPages = pagination?.last_page ?? 0
  const totalCount = pagination?.total ?? 0

  const renderPagination = () => {
    if (totalPages <= 1) return null

    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)

    const pageItems = []

    pageItems.push(
      <Pagination.Prev
        key='prev'
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      />
    )

    if (start > 1) {
      pageItems.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      )
      if (start > 2) pageItems.push(<Pagination.Ellipsis key='ellipsis-start' disabled />)
    }

    for (let i = start; i <= end; i++) {
      pageItems.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      )
    }

    if (end < totalPages) {
      if (end < totalPages - 1) pageItems.push(<Pagination.Ellipsis key='ellipsis-end' disabled />)
      pageItems.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      )
    }

    pageItems.push(
      <Pagination.Next
        key='next'
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      />
    )

    return (
      <div className='d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2'>
        <div className='text-muted small'>
          {pagination?.from}–{pagination?.to} / {totalCount}
        </div>
        <Pagination className='mb-0'>{pageItems}</Pagination>
      </div>
    )
  }

  const breadcrumbs = [
    {title: intl.formatMessage({id: 'MENU.WORKPLAN'}), path: '/workplan/activity-report'},
  ]

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'MENU.WORKPLAN.REPORTPHOTOS'})}
      </PageTitle>

      <KTCard className='mb-5'>
        <KTCardBody>
          <Row className='g-3 align-items-end'>
            <Col xs={12} sm={4}>
              <Form.Label className='col-lg-12 fw-bold fs-6 mb-2'>{intl.formatMessage({id: 'PAYMENT.PROJECT'})}</Form.Label>
              <Form.Select className='form-select-solid' value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value=''>-- {intl.formatMessage({id: 'FILTER.ALL'})} --</option>
                {projectLookupList &&
                  projectLookupList.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Label className='col-lg-12 fw-bold fs-6 mb-2'>{intl.formatMessage({id: 'EXPLANATION'})}</Form.Label>
              <Form.Control
                type='text'
                value={description}
                className='form-control form-control-solid mb-3 mb-lg-0'
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
              />
            </Col>
            <Col xs={12} sm='auto'>
              <Button variant='primary' onClick={handleFilter}>
                {intl.formatMessage({id: 'BUTTON.SEARCH'})}
              </Button>
            </Col>
          </Row>
        </KTCardBody>
      </KTCard>

      <KTCard>
        <KTCardBody>
          {error && <Alert variant='danger'>{error?.message || String(error)}</Alert>}

          {loading && items.length === 0 && <ListLoading />}

          {!loading && items.length === 0 && (
            <div className='text-center text-muted py-10'>
              {intl.formatMessage({id: 'WORKPLAN.REPORTPHOTOS.EMPTY'})}
            </div>
          )}

          {items.length > 0 && (
            <>
              <Fancybox options={{Carousel: {infinite: false}}}>
                <Row className='row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-4 g-4'>
                  {items.map((item) => (
                    <Col key={item.id}>
                      <Card className='h-100 shadow-sm'>
                        <a
                          data-fancybox='report-gallery'
                          data-caption={item.description}
                          href={item.fileUrl}
                          style={{display: 'block', overflow: 'hidden'}}
                        >
                          <Card.Img
                            variant='top'
                            src={item.fileUrl}
                            style={{height: '160px', objectFit: 'cover'}}
                          />
                        </a>
                        <Card.Body className='p-3'>
                          <div
                            className='fw-semibold text-truncate mb-1'
                            title={item.description}
                          >
                            {item.description || '-'}
                          </div>
                          {item.projectName && (
                            <div className='text-muted small mb-1'>{item.projectName}</div>
                          )}
                          <div className='text-muted small mb-2'>
                            {item.noteDate
                              ? intl.formatDate(item.noteDate, {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                })
                              : '-'}
                          </div>
                          <div className='d-flex gap-2'>
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary flex-grow-1'
                              onClick={() => handleDownload(item.fileUrl, item.fileName)}
                            >
                              {intl.formatMessage({id: 'BUTTON.DOWNLOAD'})}
                            </button>
                            {item.activityReportId > 0 && (
                              <a
                                href={`/workplan/activity-report/detail/${item.activityReportId}`}
                                className='btn btn-sm btn-light-secondary'
                                target='_blank'
                                rel='noreferrer'
                              >
                                {intl.formatMessage({id: 'BUTTON.DETAIL'})}
                              </a>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Fancybox>

              {renderPagination()}
            </>
          )}
        </KTCardBody>
      </KTCard>

    </>
  )
}

export {List}
